import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Agenda from 'react-big-calendar/lib/Agenda'
import { inRange } from 'react-big-calendar/lib/utils/eventLevels'
import { isSelected } from 'react-big-calendar/lib/utils/selection'

import { EMPTY_VALUE } from '@/constants/ui'

import { getBuildingTimezoneFromEvent, getDates } from '../../helpers/helpers'
import { isFullDayTimeBlock } from './helpers'

const DEFAULT_LENGTH = 30

// Multi-day reservations are flagged by getMultidayAmenities; split time blocks keep the
// block's original start in startDate (see splitTimeBlockIntoDays)
const isMultidayEvent = (event) =>
  event.isMultiday || (event.reservationType === 'timeBlock' && event.endDate > event.startDate)

// Port of react-big-calendar 1.18's built-in Agenda view. Differences:
// - days without events still get a row, with EMPTY_VALUE in the time and event cells
// - the table is always rendered (the built-in view swaps it for "no events in range")
// Statics (range/navigate/title) are reused from the built-in view at the bottom.
const AgendaWithEmptyDays = ({
  accessors,
  components,
  date,
  events,
  getters,
  length = DEFAULT_LENGTH,
  localizer,
  onDoubleClickEvent,
  onSelectEvent,
  selected,
}) => {
  const headerRef = useRef(null)
  const dateColRef = useRef(null)
  const timeColRef = useRef(null)
  const contentRef = useRef(null)
  const tbodyRef = useRef(null)

  // Keeps the header table's Date/Time columns as wide as the body's (they are separate tables)
  useEffect(() => {
    const firstRow = tbodyRef.current?.firstChild
    if (!firstRow) return

    dateColRef.current.style.width = `${firstRow.children[0].getBoundingClientRect().width}px`
    timeColRef.current.style.width = `${firstRow.children[1].getBoundingClientRect().width}px`

    const content = contentRef.current
    const isOverflowing = content.scrollHeight > content.clientHeight
    headerRef.current.classList.toggle('rbc-header-overflowing', isOverflowing)
    headerRef.current.style.marginRight = isOverflowing
      ? `${content.offsetWidth - content.clientWidth}px`
      : ''
  })

  const messages = localizer.messages
  const end = localizer.add(date, length - 1, 'day')
  const range = localizer.range(date, end, 'day')

  const eventsInRange = events
    .filter((event) =>
      inRange(
        event,
        localizer.startOf(date, 'day'),
        localizer.endOf(end, 'day'),
        accessors,
        localizer,
      ),
    )
    .sort((a, b) => +accessors.start(a) - +accessors.start(b))

  const renderTimeLabel = (day, event) => {
    const TimeComponent = components.time
    const start = accessors.start(event)
    const eventEnd = accessors.end(event)
    const isAllDay =
      accessors.allDay(event) ||
      (event.reservationType === 'timeBlock' && isFullDayTimeBlock(event))
    let label = messages.allDay
    let labelClass = ''

    // A multi-day item shows its full date range on every day it covers
    if (isMultidayEvent(event)) {
      label = getDates(event, getBuildingTimezoneFromEvent(event))
    } else if (!isAllDay) {
      if (localizer.eq(start, eventEnd)) {
        label = localizer.format(start, 'agendaTimeFormat')
      } else if (localizer.isSameDate(start, eventEnd)) {
        label = localizer.format({ end: eventEnd, start }, 'agendaTimeRangeFormat')
      } else if (localizer.isSameDate(day, start)) {
        label = localizer.format(start, 'agendaTimeFormat')
      } else if (localizer.isSameDate(day, eventEnd)) {
        label = localizer.format(eventEnd, 'agendaTimeFormat')
      }
    }

    if (localizer.gt(day, start, 'day')) labelClass = 'rbc-continues-prior'
    if (localizer.lt(day, eventEnd, 'day')) labelClass += ' rbc-continues-after'

    return (
      <span className={labelClass.trim()}>
        {TimeComponent ? <TimeComponent day={day} event={event} label={label} /> : label}
      </span>
    )
  }

  const renderDay = (day, dayKey) => {
    const EventComponent = components.event
    const AgendaDate = components.date
    const dateLabel = localizer.format(day, 'agendaDateFormat')
    const renderDateCell = (rowSpan) => (
      <td className="rbc-agenda-date-cell" rowSpan={rowSpan}>
        {AgendaDate ? <AgendaDate day={day} label={dateLabel} /> : dateLabel}
      </td>
    )

    const dayEvents = eventsInRange.filter((event) =>
      inRange(
        event,
        localizer.startOf(day, 'day'),
        localizer.endOf(day, 'day'),
        accessors,
        localizer,
      ),
    )

    if (dayEvents.length === 0) {
      return (
        <tr className="rbc-agenda-empty-day" key={`${dayKey}_empty`}>
          {renderDateCell(1)}
          <td className="rbc-agenda-time-cell">{EMPTY_VALUE}</td>
          <td className="rbc-agenda-event-cell">{EMPTY_VALUE}</td>
        </tr>
      )
    }

    return dayEvents.map((event, idx) => {
      const title = accessors.title(event)
      const { className, style } = getters.eventProp(
        event,
        accessors.start(event),
        accessors.end(event),
        isSelected(event, selected),
      )

      return (
        <tr className={className} key={`${dayKey}_${idx}`} style={style}>
          {idx === 0 && renderDateCell(dayEvents.length)}
          <td className="rbc-agenda-time-cell">{renderTimeLabel(day, event)}</td>
          <td
            className="rbc-agenda-event-cell"
            onClick={(e) => onSelectEvent?.(event, e)}
            onDoubleClick={(e) => onDoubleClickEvent?.(event, e)}
          >
            {EventComponent ? <EventComponent event={event} title={title} /> : title}
          </td>
        </tr>
      )
    })
  }

  return (
    <div className="rbc-agenda-view">
      <table className="rbc-agenda-table" ref={headerRef}>
        <thead>
          <tr>
            <th className="rbc-header" ref={dateColRef}>
              {messages.date}
            </th>
            <th className="rbc-header" ref={timeColRef}>
              {messages.time}
            </th>
            <th className="rbc-header">{messages.event}</th>
          </tr>
        </thead>
      </table>
      <div className="rbc-agenda-content" ref={contentRef}>
        <table className="rbc-agenda-table">
          <tbody ref={tbodyRef}>{range.map((day, idx) => renderDay(day, idx))}</tbody>
        </table>
      </div>
    </div>
  )
}

// react-big-calendar calls these on the view component for the toolbar label and navigation
AgendaWithEmptyDays.range = Agenda.range
AgendaWithEmptyDays.navigate = Agenda.navigate
AgendaWithEmptyDays.title = Agenda.title

AgendaWithEmptyDays.propTypes = {
  accessors: PropTypes.object.isRequired,
  components: PropTypes.object.isRequired,
  date: PropTypes.instanceOf(Date).isRequired,
  events: PropTypes.array.isRequired,
  getters: PropTypes.object.isRequired,
  length: PropTypes.number,
  localizer: PropTypes.object.isRequired,
  onDoubleClickEvent: PropTypes.func,
  onSelectEvent: PropTypes.func,
  selected: PropTypes.object,
}

export default AgendaWithEmptyDays
